import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import CostCenterDetail from './CostCenterDetail';
import { fmtMoney, fmtHours, fmtNumber } from './format';
import { c, chart } from '../../theme';

// scopeParams carries no leading '&' and filterQ always does, so neither can be
// concatenated onto the other blindly -- `?&lens=fleet` is not a query string,
// and dropping the wrong '&' silently drops a filter.
const qs = (...parts) => parts.filter(Boolean).map((x) => x.replace(/^&/, '')).join('&');

// Trend view for a single cost center — a bar per pay run INSIDE the span the
// board is on (oldest → newest), each clickable to narrow the detail below to
// that one run.
//
// Both halves of that sentence were wrong until the span was passed down. The
// endpoint took no scope, so the bars were every pay run ever loaded, and this
// opened on the newest of them. A reader who had selected July was therefore
// shown August's run by default, under a heading that said July -- and because
// nothing here moved when the board moved, the same figures appeared under
// "July" and under "year to date". That is the shape of error this panel is
// least able to survive: two different questions, one answer, no disagreement
// visible anywhere on screen.
export default function CostCenterTrend({ ccId, ccName, scopeParams = '', filterQ = '', canViewPeople, onSelectEmployee }) {
  // null is the whole span, and it is what this opens on: the span is what the
  // row above it totals, so the panel and the row now agree before anyone
  // clicks anything.
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  // A pay run picked inside one span is not in the next one. Without this,
  // switching months left the previous month's run selected and the detail
  // below answering for a period the trend no longer draws.
  useEffect(() => { setSelectedPeriodId(null); }, [scopeParams, filterQ, ccId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-trend', ccId, scopeParams, filterQ],
    queryFn:  () => api.get(`/overtime/costcenter/${encodeURIComponent(ccId)}/trend?${qs(scopeParams, filterQ)}`),
    enabled:  !!ccId,
  });

  if (isLoading) return <div style={s.wrap}><LoadingSpinner /></div>;
  if (error)     return <div style={s.wrap}><div style={s.err}>{error.message}</div></div>;

  const periods = data?.periods || [];

  if (!periods.length) {
    return (
      <div style={s.wrap}>
        <div style={s.empty}>No overtime history found for this cost center.</div>
      </div>
    );
  }

  // No fallback to the newest bar. An unselected trend means the span, and
  // defaulting to a period here is exactly what made the panel disagree with
  // the row it sits under.
  const activePeriodId = selectedPeriodId;
  const activePeriod   = periods.find((p) => p.period_id === activePeriodId) || null;

  // Employee counts are DISTINCT per period and cannot be added up — a driver
  // with overtime in three runs would count three times. Hours and cost are
  // additive; the head count for the span comes from the detail below, which
  // counts it once.
  const spanTotal = periods.reduce(
    (a, p) => ({ ot_hours: a.ot_hours + p.ot_hours, ot_amount: a.ot_amount + p.ot_amount }),
    { ot_hours: 0, ot_amount: 0 },
  );

  const chartData = periods.map((p) => ({
    ...p,
    // Short label: "Jun 30" style from period_end date
    shortLabel: p.period_end
      ? new Date(String(p.period_end).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : p.label,
  }));

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>{ccName} — Trend</div>
        <div style={s.subtitle}>
          {activePeriod
            ? `${activePeriod.label} · click the bar again for all ${periods.length}`
            : `${periods.length} pay run${periods.length !== 1 ? 's' : ''} in view · click a bar to narrow to one`}
        </div>
      </div>

      {/* Trend bar chart */}
      <div style={s.chartWrap}>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis
              dataKey="shortLabel"
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              tickFormatter={fmtMoney}
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false} width={64}
            />
            <Tooltip
              cursor={{ fill: 'rgba(27,42,74,0.05)' }}
              formatter={(v) => [fmtMoney(v), 'OT Cost']}
              labelFormatter={(_l, p) => p?.[0]?.payload?.label || ''}
              contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }}
            />
            <Bar dataKey="ot_amount" radius={[4, 4, 0, 0]} cursor="pointer"
                 onClick={(d) => setSelectedPeriodId((cur) => (cur === d.period_id ? null : d.period_id))}>
              {chartData.map((p) => (
                <Cell
                  key={p.period_id}
                  fill={!activePeriodId || p.period_id === activePeriodId
                    ? chart.emphasis : chart.recessive}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Period summary strip */}
      {periods.length > 0 && (
        <div style={s.strip}>
          <div style={s.stripItem}>
            <div style={s.stripLabel}>{activePeriod ? 'Period' : 'Showing'}</div>
            <div style={s.stripVal}>
              {activePeriod ? activePeriod.label
                            : `${periods.length} pay run${periods.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <div style={s.stripItem}>
            <div style={s.stripLabel}>OT Cost</div>
            <div style={s.stripVal}>{fmtMoney((activePeriod || spanTotal).ot_amount)}</div>
          </div>
          <div style={s.stripItem}>
            <div style={s.stripLabel}>OT Hours</div>
            <div style={s.stripVal}>{fmtHours((activePeriod || spanTotal).ot_hours)}</div>
          </div>
          {/* Only for a single run. Across a span this is a DISTINCT count the
              bars cannot supply, and summing them would count a driver once
              per pay run they worked. The detail below states it correctly. */}
          {activePeriod && (
            <div style={s.stripItem}>
              <div style={s.stripLabel}>Employees</div>
              <div style={s.stripVal}>{fmtNumber(activePeriod.employee_count)}</div>
            </div>
          )}
        </div>
      )}

      {/* Detail for the selected bar, or for the whole span when none is. The
          employee modal is opened on the same scope, so a person's total there
          matches the row that was clicked to reach it. */}
      <CostCenterDetail filterQ={filterQ}
        ccId={ccId}
        ccName={ccName}
        periodId={activePeriodId}
        scopeParams={scopeParams}
        canViewPeople={canViewPeople}
        onSelectEmployee={onSelectEmployee ? (eId) => onSelectEmployee(eId, activePeriodId) : undefined}
        embedded
      />
    </div>
  );
}

const s = {
  wrap:       { background: '#F9FAFB' },
  header:     { display: 'flex', alignItems: 'baseline', gap: '12px', padding: '16px 18px 0', flexWrap: 'wrap' },
  title:      { fontSize: '14px', fontWeight: 700, color: c.navy },
  subtitle:   { fontSize: '11px', color: '#9CA3AF' },
  chartWrap:  { padding: '10px 18px 4px' },
  strip: {
    display: 'flex', gap: '0', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB',
    background: '#fff', margin: '0 0 0 0',
  },
  stripItem:  { flex: 1, padding: '10px 14px', borderRight: '1px solid #F3F4F6' },
  stripLabel: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF', marginBottom: '2px' },
  stripVal:   { fontSize: '13px', fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' },
  empty:      { padding: '32px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' },
  err:        { padding: '16px', color: '#DC2626', fontSize: '13px' },
};
